<template>
  <div class="space-y-5">
    <p class="form-helper">
      Your header’s look comes from the template — here you control the logo, the navigation
      links (including dropdowns), and the call-to-action button(s).
    </p>

    <!-- Logo -->
    <section class="space-y-2">
      <span class="text-overline">Logo</span>
      <div class="flex items-center gap-2">
        <input
          v-model="model.logo.image_url"
          type="text"
          class="form-input flex-1"
          placeholder="https://…/logo.png">
        <button
          type="button"
          :disabled="logoUploading || !editorApi"
          class="btn btn-secondary btn-sm"
          @click="logoFileInput.click()">
          {{ logoUploading ? 'Uploading…' : 'Upload' }}
        </button>
        <input ref="logoFileInput" type="file" accept="image/*" class="hidden" @change="uploadLogo">
      </div>
      <span class="form-helper">Leave blank to use your business name as the wordmark.</span>
    </section>

    <!-- Nav items -->
    <section class="space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-overline">Navigation</span>
        <button type="button" class="btn btn-ghost btn-xs" @click="addItem">
          <EditorIcon name="plus" class="w-3.5 h-3.5" /> Link
        </button>
      </div>

      <div
        v-for="(item, i) in model.nav"
        :key="i"
        class="card card-body space-y-3">
        <div class="flex items-center gap-2">
          <input
            v-model="item.label"
            type="text"
            class="form-input flex-1"
            placeholder="Link label">
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs"
            title="Move up"
            :disabled="i === 0"
            @click="moveItem(i, -1)">
            <EditorIcon name="arrow-up" class="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs"
            title="Move down"
            :disabled="i === model.nav.length - 1"
            @click="moveItem(i, 1)">
            <EditorIcon name="arrow-down" class="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-icon btn-xs text-error"
            title="Remove"
            @click="model.nav.splice(i, 1)">
            <EditorIcon name="trash" class="w-3.5 h-3.5" />
          </button>
        </div>

        <label class="block">
          <span class="form-label">Links to</span>
          <input v-model="item.url" type="text" class="form-input w-full mt-1" placeholder="/services or https://…">
        </label>

        <label class="block">
          <span class="form-label">Dropdown</span>
          <select v-model="item.source" class="form-select w-full mt-1">
            <option value="manual">{{ item.children.length ? 'Custom dropdown' : 'No dropdown' }}</option>
            <option value="services">{{ sourceLabel('services') }}</option>
            <option value="service_areas">{{ sourceLabel('service_areas') }}</option>
          </select>
        </label>

        <!-- Auto dropdown controls -->
        <div v-if="item.source !== 'manual'" class="space-y-2">
          <p class="alert alert-info text-xs">
            Dropdown auto-syncs to your {{ item.source === 'services' ? 'services' : 'service areas' }}
            ({{ sourceCount(item.source) }} live).
          </p>
          <div class="flex items-center justify-between gap-2">
            <span class="form-label">Max items</span>
            <input v-model.number="item.max_items" type="number" min="1" max="20" class="form-input w-20">
          </div>
          <label class="flex items-center justify-between gap-2">
            <span class="text-sm text-gray-800">Show a “View all” link</span>
            <input v-model="item.show_all" type="checkbox" class="form-checkbox">
          </label>
        </div>

        <!-- Manual dropdown children -->
        <div v-else class="space-y-2">
          <LinkRows :links="item.children" />
          <button type="button" class="btn btn-ghost btn-xs" @click="addChild(item)">
            <EditorIcon name="plus" class="w-3.5 h-3.5" /> Dropdown link
          </button>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section v-if="model.cta.length" class="space-y-2">
      <span class="text-overline">Buttons</span>
      <p class="form-helper">
        Your header call-to-action button(s). An external URL opens in a new tab.
      </p>
      <div
        v-for="(c, ci) in model.cta"
        :key="ci"
        class="flex items-center gap-2">
        <input v-model="c.label" type="text" class="form-input flex-1" placeholder="Book a Visit">
        <input v-model="c.url" type="text" class="form-input flex-1" placeholder="/contact or https://…">
      </div>
    </section>

    <button
      type="button"
      :disabled="saving"
      class="btn btn-primary btn-sm btn-full"
      @click="save">
      {{ saving ? 'Saving…' : 'Save header' }}
    </button>
  </div>
</template>

<script setup>
import { computed, h, inject, reactive, ref, watch } from 'vue'
import EditorIcon from './EditorIcon.vue'

const props = defineProps({
  header: { type: Object, required: true },
  saving: { type: Boolean, default: false }
})
const emit = defineEmits(['save'])

const editorApi = inject('templateEditorApi', null)
const logoFileInput = ref(null)
const logoUploading = ref(false)

// Inline list-of-{text,url} rows with reorder + remove (shared shape with the
// footer editor; declared inline to keep this a single file).
const LinkRows = {
  props: { links: { type: Array, required: true } },
  setup(p) {
    const move = (i, d) => {
      const j = i + d
      if (j < 0 || j >= p.links.length) return
      const [row] = p.links.splice(i, 1)
      p.links.splice(j, 0, row)
    }
    return () => p.links.map((link, i) => h('div', { key: i, class: 'flex items-center gap-2' }, [
      h('input', { value: link.text, type: 'text', class: 'form-input flex-1', placeholder: 'Label', onInput: (e) => { link.text = e.target.value } }),
      h('input', { value: link.url, type: 'text', class: 'form-input flex-1', placeholder: '/page or https://…', onInput: (e) => { link.url = e.target.value } }),
      h('button', { type: 'button', class: 'btn btn-ghost btn-icon btn-xs', title: 'Move up', disabled: i === 0, onClick: () => move(i, -1) }, h(EditorIcon, { name: 'arrow-up', class: 'w-3.5 h-3.5' })),
      h('button', { type: 'button', class: 'btn btn-ghost btn-icon btn-xs', title: 'Move down', disabled: i === p.links.length - 1, onClick: () => move(i, 1) }, h(EditorIcon, { name: 'arrow-down', class: 'w-3.5 h-3.5' })),
      h('button', { type: 'button', class: 'btn btn-ghost btn-icon btn-xs text-error', title: 'Remove', onClick: () => p.links.splice(i, 1) }, h(EditorIcon, { name: 'trash', class: 'w-3.5 h-3.5' }))
    ]))
  }
}

function blankModel(content) {
  return {
    logo: { image_url: content.logo?.image_url || '', alt: content.logo?.alt || '' },
    nav: (content.nav || []).map((n) => ({
      id: n.id || '',
      label: n.label || '',
      url: n.url || '',
      source: n.source || 'manual',
      max_items: n.max_items || 6,
      show_all: n.show_all !== false,
      children: (n.children || []).map((c) => ({ text: c.text || '', url: c.url || '' }))
    })),
    cta: (content.cta || []).map((c) => ({ label: c.label || '', url: c.url || '' }))
  }
}

const model = reactive(blankModel(props.header.content || {}))

watch(() => props.header.content, (content) => {
  Object.assign(model, blankModel(content || {}))
})

const sources = computed(() => props.header.sources || {})
function sourceLabel(key) { return sources.value[key]?.label || key }
function sourceCount(key) { return sources.value[key]?.count ?? 0 }

function slugify(text) {
  return (text || 'item').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'item'
}

function addItem() {
  model.nav.push({ id: '', label: 'New link', url: '/', source: 'manual', max_items: 6, show_all: true, children: []})
}
function moveItem(i, d) {
  const j = i + d
  if (j < 0 || j >= model.nav.length) return
  const [item] = model.nav.splice(i, 1)
  model.nav.splice(j, 0, item)
}
function addChild(item) { item.children.push({ text: '', url: '' }) }

async function uploadLogo(e) {
  const file = e.target.files && e.target.files[0]
  if (!file || !editorApi) return
  logoUploading.value = true
  try {
    const res = await editorApi.uploadImage(file, model.logo.alt || 'Logo')
    if (res && res.url) model.logo.image_url = res.url
  } finally {
    logoUploading.value = false
    e.target.value = ''
  }
}

function save() {
  emit('save', {
    logo: model.logo,
    nav: model.nav.map((n) => ({
      id: n.id || slugify(n.label),
      label: n.label,
      url: n.url,
      source: n.source,
      max_items: n.max_items,
      show_all: n.show_all,
      children: n.source === 'manual' ? n.children : []
    })),
    cta: model.cta
  })
}
</script>
